import { useMemo } from "react";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { DashboardMonitoringView } from "@/features/deployment-dashboard/overview/dashboard-monitoring-view";
import { useDashboardMonitoringData } from "@/features/deployment-dashboard/overview/hooks/use-dashboard-monitoring-data";
import { useLiveTracksStream } from "@/features/deployment-dashboard/live-view/hooks/use-live-tracks-stream";
import { useAreas } from "@/features/deployment-dashboard/shared/hooks/use-areas";
import { calculateDerivedPopulation } from "@/features/deployment-dashboard/shared/population-metrics";
import type { DashboardAreaMetric } from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";

export function DashboardMonitoringContainer() {
  const { deploymentId, selectedDeployment } = useSelectedDeployment();
  const { data } = useDashboardMonitoringData({
    deploymentId,
    deployment: selectedDeployment,
  });
  const { data: areas = [] } = useAreas(deploymentId);
  const derivedArea = areas.find(
    (area) => area.derivedPopulationMetrics?.duty_free_population != null
  );
  const liveTracks = useLiveTracksStream(
    deploymentId,
    derivedArea?.id ?? null,
    derivedArea != null,
    1
  ) ?? { latestAreaFrame: null, status: "disabled" as const };
  const dashboardData = useMemo(() => {
    const areaById = new Map(areas.map((area) => [area.id, area]));
    const derivedPopulationStatus: DashboardAreaMetric =
      derivedArea == null
        ? { status: "empty", value: null }
        : liveTracks.status === "connecting" || liveTracks.status === "retrying"
          ? { status: "loading", value: null }
          : liveTracks.latestAreaFrame == null
            ? { status: "unavailable", value: null }
            : { status: "ready", value: liveTracks.latestAreaFrame.objects.length };

    return {
      ...data,
      areas: {
        ...data.areas,
        data:
          data.areas.data?.map((row) => {
            const area = areaById.get(row.id);
            const derivedPopulation =
              area?.id === derivedArea?.id
                ? {
                    status:
                      row.population.status === "stale"
                        ? ("stale" as const)
                        : row.population.status !== "ready"
                          ? row.population.status
                          : derivedPopulationStatus.status,
                    value: calculateDerivedPopulation(area?.derivedPopulationMetrics, {
                      totalPopulation: row.population.value,
                      departuresLoungePopulation: derivedPopulationStatus.value,
                    }),
                  }
                : { status: "empty" as const, value: null };
            return {
              ...row,
              derivedPopulationMetrics: area?.derivedPopulationMetrics,
              derivedPopulation,
            };
          }) ?? null,
      },
    };
  }, [areas, data, derivedArea, liveTracks.latestAreaFrame, liveTracks.status]);

  return <DashboardMonitoringView deploymentId={deploymentId} data={dashboardData} />;
}
